import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Wallet, Building, Smartphone } from 'lucide-react';
import { animations } from '../animations';
import { 
  Header,
  Body,
  FormGroup,
  Label,
  SummaryCard,
  ButtonGroup,
  Button 
} from '../StyledComponents';

export const HomeCarePaymentStep = ({
  selectedService,
  paymentMethod,
  onPaymentMethodChange,
  onPrevious,
  onSubmit,
  isLoading
}) => {
  const paymentOptions = [
    { 
      value: 'Cash', 
      label: 'Cash Payment', 
      icon: Wallet, 
      description: 'Pay with cash when service is completed' 
    },
    { 
      value: 'Card', 
      label: 'Card Payment', 
      icon: CreditCard, 
      description: 'Pay with credit/debit card' 
    },
    { 
      value: 'Mobile', 
      label: 'Mobile Payment', 
      icon: Smartphone, 
      description: 'Pay with mobile wallet (Tele-birr, etc.)' 
    },
    { 
      value: 'Corporate', 
      label: 'Corporate Account', 
      icon: Building, 
      description: 'Use company account balance' 
    }
  ];

  return (
    <motion.div {...animations.pageTransition}>
      <Header>
        <CreditCard />
        <h2>Payment & Confirmation</h2>
      </Header>
      <Body>
        {/* Service Summary */}
        <SummaryCard style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#16a34a', marginBottom: '1rem' }}>Service Summary</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Service Type:</span>
            <strong>{selectedService?.serviceName}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Duration:</span>
            <strong>60 minutes</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Available Providers:</span>
            <strong>{selectedService?.availableDrivers} provider(s)</strong>
          </div>
          <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem' }}>
            <span>Total Price:</span>
            <strong style={{ color: '#16a34a' }}>${selectedService?.fixedPrice}</strong>
          </div>
        </SummaryCard>

        {/* Payment Method Selection */}
        <FormGroup>
          <Label>Choose Payment Method</Label>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            {paymentOptions.map(option => (
              <label 
                key={option.value} 
                style={{
                  margin: '0 0 15px 0',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '8px',
                  border: paymentMethod === option.value ? '2px solid #16a34a' : '1px solid #e2e8f0',
                  backgroundColor: paymentMethod === option.value ? '#f0fdf4' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value={option.value}
                  checked={paymentMethod === option.value}
                  onChange={() => onPaymentMethodChange(option.value)}
                  style={{ cursor: 'pointer' }}
                />
                <option.icon size={20} style={{ color: '#16a34a', marginLeft: '12px' }} />
                <div style={{ marginLeft: '15px' }}>
                  <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem' }}>
                    {option.label}
                  </h4>
                  <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                    {option.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </FormGroup>

        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9rem' }}>
            <strong>Note:</strong> Once you submit this request, we will immediately start looking for available home care providers in your area. You will receive real-time updates about your request status.
          </p>
        </div>

        <ButtonGroup>
          <Button variant="secondary" onClick={onPrevious} disabled={isLoading}>
            Previous
          </Button>
          <Button
            variant="success"
            onClick={onSubmit}
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{ 
              background: '#16a34a',
              color: 'white'
            }}
          >
            {isLoading ? (
              <>
                <div className="spinning">⟳</div>
                Submitting Request...
              </>
            ) : (
              'Submit Home Care Request'
            )}
          </Button>
        </ButtonGroup>
      </Body>
    </motion.div>
  );
};
